<template>
  <div class="m-4 room-list">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Rooms & Beds</h5>
      <b-button
        class="pt-2 pb-2"
        size="sm"
        variant="primary"
        @click="showFormAdd"
      >
        New Room
      </b-button>
    </div>
    <v-select
      v-if="locations.length > 0"
      placeholder="Select Location"
      v-model="location"
      :options="locations"
      class="mb-3"
      style="max-width: 300px"
      @input="getRooms"
    ></v-select>
    <grid
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="rooms"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    ></grid>
    <b-modal
      centered
      no-close-on-backdrop
      :title="roomId ? 'Edit Room' : 'Add Room'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitRoom"
      v-model="showEditor"
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-form-group
        label="Name"
        :state="formErrors.name.length == 0 ? null : false"
        :invalid-feedback="formErrors.name.join(', ')"
      >
        <b-form-input
          v-model="form.name"
          maxLength="40"
          :state="formErrors.name.length == 0 ? null : false"
        ></b-form-input>
      </b-form-group>
      <b-row v-if="locations.length > 0 && location">
        <b-col>
          <span>Location: {{ location.label }} </span>
        </b-col>
      </b-row>
      <b-form-group label="Beds">
        <tags-input v-model="form.beds" :listDraggable="true" maxLength="40" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Grid from "../../third_party/grid/Grid.vue";
import TagsInput from "../controls/TagsInput.vue";
import { validateRequired } from "../../helpers/validation";
import roomsServer from "../../server/room";

export default {
  name: "RoomCrud",
  components: { Grid, TagsInput },
  props: ["showing"],
  data() {
    return {
      locationId: null,
      roomId: null,
      rooms: [],
      form: {
        name: null,
        locationId: null,
        beds: [],
        bedObjects: []
      },
      formErrors: {
        name: []
      },
      showEditor: false
    };
  },
  mounted() {
    if (this.locations.length > 0) {
      this.location = this.locations[0];
    }
    this.fetchLocations();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", ["fetchLocations"]),
    async getRooms() {
      this.rooms = await roomsServer.query({
        locationId: this.locationId
      });
    },
    async deleteRoom(id) {
      await roomsServer.delete(id);
      this.showAlertSuccess();
      await this.getRooms();
    },
    async submitRoom(modalEvent) {
      modalEvent.preventDefault();
      if (this.validateForm()) {
        const data = {
          name: this.form.name,
          locationId: this.locationId,
          beds: this.form.beds.map(bed => {
            const foundBed = this.form.bedObjects.find(b => b.name == bed);
            if (foundBed) return foundBed;
            return {
              name: bed
            };
          })
        };
        if (this.roomId) {
          await roomsServer.update(this.roomId, data);
        } else {
          await roomsServer.create(data);
        }
        this.showEditor = false;
        this.getRooms();
        this.showAlertSuccess();
      }
    },
    validateForm() {
      let withoutErrors = true;

      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    showFormAdd() {
      this.roomId = null;
      this.form = {
        name: "",
        beds: [],
        bedObjects: []
      };
      this.showEditor = true;
    },
    showFormEdit(room) {
      this.roomId = room.id;
      this.form = {
        name: room.name,
        beds: room.beds.map(b => b.name),
        bedObjects: room.beds
      };
      this.showEditor = true;
    }
  },
  computed: {
    ...mapState("client", {
      locations: state =>
        state.locations.map(l => ({
          value: l.id,
          label: l.name
        }))
    }),
    location: {
      get() {
        return this.locations.find(l => l.value === this.locationId);
      },
      set(newValue) {
        this.locationId = newValue ? newValue.value : null;
      }
    },
    columns() {
      return [
        {
          text: "Name",
          field: "name",
          flex: 1
        },
        {
          text: "Beds",
          flex: 1,
          sortable: false,
          type: "template",
          template: ({ record }) => {
            return record.beds.map(b => b.name).join(", ");
          }
        },
        {
          type: "action",
          cellCls: "pointer-cell",
          align: "right",
          width: 100,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: ({ record }) => {
                this.showFormEdit(record.data);
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2" src="${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => await this.deleteRoom(record.data.id))
                  .catch(function() {});
              }
            }
          ]
        }
      ];
    }
  },
  watch: {
    location: async function(newValue) {
      if (newValue) {
        await this.getRooms();
      }
    },
    showing: function(newValue) {
      if (newValue) {
        this.fetchLocations();
      }
    }
  }
};
</script>

<style></style>
